import * as React from 'react';
import {
    Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import { getMockVirtualCity, runAdjustMatchStartDatesScript, runGenerateMatchBetsScript, runSimulateMatchScript } from '../../Api';

interface ScriptOutcome {
    message: string;
    /** A place to go once the action has run, such as the live view of a match it created. */
    link?: { label: string; url: string };
}

interface ScriptDefinition {
    key: string;
    title: string;
    description: string;
    run: () => Promise<ScriptOutcome>;
    buttonLabel?: string;
    icon?: React.ReactNode;
}

const SCRIPTS: ScriptDefinition[] = [
    {
        key: 'adjust-start-dates',
        title: 'Adjust start dates',
        description: "Goes through every scheduled match and snaps any start date that isn't on the "
            + '00/15/30/45-minute grid forward to the nearest compliant time.',
        run: async () => {
            const outcome = await runAdjustMatchStartDatesScript();
            return {
                message: outcome.adjustedCount === 0
                    ? 'No scheduled matches needed adjustment.'
                    : `Adjusted ${outcome.adjustedCount} scheduled match start date(s) onto the 15-minute grid.`,
            };
        },
    },
    {
        key: 'generate-bets',
        title: 'Generate bets for matches',
        description: 'Goes through scheduled matches with no bets at all and generates them using the '
            + 'existing odds logic.',
        run: async () => {
            const outcome = await runGenerateMatchBetsScript();
            return {
                message: outcome.generatedCount === 0
                    ? 'No scheduled matches were missing bets.'
                    : `Generated bets for ${outcome.generatedCount} scheduled match(es).`,
            };
        },
    },
    {
        key: 'simulate-match',
        title: 'Simulate a match now',
        description: 'Creates a friendly Virtual Football match between two teams of an existing friendly '
            + 'league, kicking off one minute from now with its replay already generated, and opens its '
            + 'live view. For testing the live renderer without waiting for a scheduled fixture.',
        run: async () => {
            const created = await runSimulateMatchScript();
            const kickoff = new Date(created.match.startDate).toLocaleTimeString();
            // Opened here so the common case is one click; the link below covers a blocked popup.
            window.open(created.liveUrl, '_blank', 'noopener,noreferrer');
            return {
                message: `${created.match.homeTeam.name} vs ${created.match.awayTeam.name} `
                    + `(${created.match.league.name}) kicks off at ${kickoff}.`,
                link: { label: 'Open live view', url: created.liveUrl },
            };
        },
    },
    {
        key: 'simulate-city',
        title: 'Simulate city view',
        description: 'Opens the mocked Ravenridge host city. Select its arena, training grounds, hotel, '
            + 'transit, clinic or civic hall, then spend demo BetCoins to evolve each building from a '
            + 'ruined provincial site into a major-event landmark.',
        buttonLabel: 'Simulate city view',
        icon: <LocationCityIcon />,
        run: async () => {
            const city = await getMockVirtualCity();
            window.open(city.liveUrl, '_blank', 'noopener,noreferrer');
            return {
                message: `${city.name} opened with ${city.buildings.length} upgradeable buildings.`,
                link: { label: 'Open city view', url: city.liveUrl },
            };
        },
    },
];

function ScriptAction(props: { script: ScriptDefinition; onRunningChange: (running: boolean) => void }) {
    const [running, setRunning] = React.useState(false);
    const [result, setResult] = React.useState<ScriptOutcome | null>(null);
    const [error, setError] = React.useState('');

    const run = async () => {
        setRunning(true);
        props.onRunningChange(true);
        setError('');
        setResult(null);
        try {
            setResult(await props.script.run());
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Could not run the script');
        } finally {
            setRunning(false);
            props.onRunningChange(false);
        }
    };

    const link = result?.link;

    return (
        <Stack spacing={1}>
            <Typography variant="subtitle1">{props.script.title}</Typography>
            <Typography color="text.secondary" variant="body2">{props.script.description}</Typography>
            {error ? <Alert severity="error">{error}</Alert> : null}
            {result ? <Alert severity="success">{result.message}</Alert> : null}
            <Stack direction="row" spacing={1}>
                <Button
                    variant="outlined"
                    startIcon={running ? <CircularProgress size={16} /> : (props.script.icon ?? <PlayArrowIcon />)}
                    onClick={run}
                    disabled={running}
                >
                    {props.script.buttonLabel ?? 'Run'}
                </Button>
                {link ? (
                    <Button
                        variant="contained"
                        startIcon={<OpenInNewIcon />}
                        onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
                    >
                        {link.label}
                    </Button>
                ) : null}
            </Stack>
        </Stack>
    );
}

export default function VirtualActionsDialog(props: { open: boolean; onClose: () => void }) {
    const [anyRunning, setAnyRunning] = React.useState(0);

    return (
        <Dialog open={props.open} onClose={anyRunning > 0 ? undefined : props.onClose} fullWidth maxWidth="sm">
            <DialogTitle>Actions</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={3} divider={<Divider />}>
                    {SCRIPTS.map(script => (
                        <ScriptAction
                            key={script.key}
                            script={script}
                            onRunningChange={running => setAnyRunning(count => count + (running ? 1 : -1))}
                        />
                    ))}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose} disabled={anyRunning > 0}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
